/*    
<summary>
   This class component is all about Managing Shift data functionality.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable } from "mobx";
import toast from "react-hot-toast";
import {
  IObservableInitialState,
  IOption,
  IOptionShiftSchedule,
} from "../../models/ICommon";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import { ICommonState } from "../../models/state/ICommonState";
import { formatMessage } from "../../translations/format-message";
import { initialState as GetAllShiftManagementState } from "../initialState/get-all-shift-state";
import * as baseService from "../service/base-service";
import { AuthStore } from "./auth-store";

import URLConstants from "../../constants/url-constants";
import { getLanguageFromLocalStorage } from "../../helpers/localstorage-helper";

import moment from "moment";
import { TimeFormatWithSeconds } from "../../constants/timePicker.constant";
import { IAddShiftManagement } from "../../models/forms/IAddShiftManagement";
import {
  BusinessVM,
  IShiftManagementList,
  IShiftManagementVM,
  ShiftSchedule,
} from "../../models/response/IShiftManagementResponse";
import { DateTimeFormatTZ } from "../../constants/time-picker-constant";
import weekdayEnum from "../../constants/enums/weekday-enum";
import { DATE_FORMAT, IsItInHoliday, JapaneseHolidays } from "../../constants/constants";
import { IStaffOption } from "../../models/response/IMasterManagementResponse";

const authStore = new AuthStore();

export class ShiftManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  shiftList: IShiftManagementVM[] = GetAllShiftManagementState.Shifts;
  shiftScheduleList: ShiftSchedule[] = [];

  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };

  addUpdateShiftManagementState = { ...this.initialStateValue };
  deleteShiftManagementState = { ...this.initialStateValue };
  deleteShiftScheduleState = { ...this.initialStateValue };
  getShiftScheduleListState = { ...this.initialStateValue };
  addShiftScheduleListState = { ...this.initialStateValue };
  addFreeShiftScheduleListState = { ...this.initialStateValue };
  publishScheduleListState = { ...this.initialStateValue };
  copyScheduleListState = { ...this.initialStateValue };
  overWriteShiftScheduleListState = { ...this.initialStateValue };
  changeShiftScheduleListState = { ...this.initialStateValue };

  activeInactiveState = { ...this.initialStateValue };

  shift: IShiftManagementVM | undefined = undefined;
  shiftState = { ...this.initialStateValue };
  shiftBusinessHours: BusinessVM[] = [];
  staffShiftOptionsState = { ...this.initialStateValue };
  allStaffOption: IStaffOption[] = [];

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      shiftList: observable,
      shiftScheduleList: observable,
      getShiftScheduleListState: observable,
      shift: observable,
      addUpdateShiftManagementState: observable,
      deleteShiftManagementState: observable,
      shiftState: observable,
      addShiftScheduleListState: observable,
      publishScheduleListState: observable,
      copyScheduleListState: observable,
      deleteShiftScheduleState: observable,
      overWriteShiftScheduleListState: observable,
      addFreeShiftScheduleListState: observable,
      changeShiftScheduleListState: observable,
      shiftBusinessHours: observable,
      staffShiftOptionsState: observable,
      allStaffOption: observable,
      GetShiftManagementListByStoreIdService: action,
      GetShiftManagementListFilterByStoreIdService: action,
      resetFreeAddShiftScheduleListState: action,
      AddShiftManagementService: action,
      UpdateShiftManagementService: action,
      UpdateShiftManagementStatusService: action,
      CopyShiftScheduleService: action,
      DeleteShiftManagementService: action,
      GetShiftManagementByIdService: action,
      PublishShiftScheduleService: action,
      UnPublishShiftScheduleService: action,
      GetAllStaffShiftOptionServiceByStoreId: action,
      ChangeShiftService: action,
      reset: action,
      resetShift: action,
      resetChangeShiftScheduleListState: action,
      GetAllShiftScheduleListByStoreIdService: action,
      GetShiftScheduleListByStoreIdDateUserIdService: action,
      GetShiftScheduleListByStoreIdDateReservationService: action,
      resetShiftManagementDetail: action,
      resetAddUpdateShiftManagementState: action,
      OverWriteShiftService: action,
      resetCopyScheduleListState: action,
      resetPublishScheduleListState: action,
      resetAddShiftScheduleListState: action,
      resetGetShiftScheduleList: action,
      AddFreeShiftScheduleService: action,
      resetGetShiftScheduleListState: action,
      resetDeleteShiftScheduleState: action,
      resetDeleteShiftManagementState: action,
      AddShiftScheduleService: action,
      resetOverWriteShiftScheduleListState: action,
      setShiftBusinessHours: action,
      resetShiftBusinessHours: action,
      allShiftManagement: computed,
      shiftDetail: computed,
      getShiftScheduleList: computed,
      getShiftScheduleDateRange: computed,
      getClosedDays: computed,
      allShiftOption: computed,
      allShiftListAssignOption: computed,
      getAllShiftStaffOptions: computed,
    });
  }

  /**
   * This function is used to Get All StaffManagements List data with pagination information by calling an API.
   * @param currentPage : Current Page Number
   * @param pagerSize : Page Size
   * @returns
   */
  GetShiftManagementListByStoreIdService = (storeId: number, businessVm: BusinessVM[] = []) => {
    this.inProgress = true;
    // const url = `${URLConstants.ShiftManagement}/List?PageNo=1&PageSize=100&Column="shift_name"&OrderBy="asc"`;
    const url = `${URLConstants.ShiftManagement}/ShiftRegistration/List?StoreId=${storeId}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IShiftManagementVM[]>>) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.shiftList = response.data.Data;
            this.shiftBusinessHours = [...businessVm];
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetAllStaffShiftOptionServiceByStoreId = (storeId: number, isCustomerLogin = false, headquarterId: null | number = null) => {
    this.staffShiftOptionsState.inProgress = true;
      const urlParams = new URLSearchParams();
      
      if(headquarterId && headquarterId > 0)
        urlParams.append("headquarterId", String(headquarterId));
      if(storeId > 0)
        urlParams.append("storeId", String(storeId));
      urlParams.append("isCustomerLogin", String(isCustomerLogin));
    const url = URLConstants.GetStaffForShiftList + "?" + urlParams.toString();
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IStaffOption[]>>) => {
        if (response.data.Error) {
          this.staffShiftOptionsState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.staffShiftOptionsState.success = true;
          this.allStaffOption = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.staffShiftOptionsState.error = err;
      })
      .finally(
        action(() => {
          this.staffShiftOptionsState.inProgress = false;
        })
      );
  };

  
  /**
   * This function is used to map allTaxOptions to allGeneral List suitable for Grid Component.
   * @returns getAllTaxOptions
   */
  get getAllShiftStaffOptions(): IOptionShiftSchedule[] {
    const options: IOptionShiftSchedule[] = [];

    if (this.allStaffOption && this.allStaffOption.length > 0) {
      this.allStaffOption.map((option: IStaffOption) => {
        options.push({
          id: option.Id,
          value: option.StaffName,
          label: option.StaffName,
          disabled: false,
          data: { ...option },
        });
      });
    }
    return options;
  }

  GetShiftManagementListFilterByStoreIdService = (storeId: number, shiftStatus: number, businessVm: BusinessVM[] = []) => {
    this.inProgress = true;
    // const url = `${URLConstants.ShiftManagement}/List?PageNo=1&PageSize=100&Column="shift_name"&OrderBy="asc"`;
    const url = `${URLConstants.ShiftManagement}/Filter?StoreId=${storeId}&shiftStatus=${shiftStatus}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IShiftManagementVM[]>>) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.shiftList = response.data.Data;
            // this.shiftBusinessHours = [...businessVm];
          }
        }
      )
      .catch((err: string) => {
        this.error = err;
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  GetAllShiftScheduleListByStoreIdService = (
    storeId: number,
    StartDate: Date,
    EndDate: Date
  ) => {
    this.getShiftScheduleListState.inProgress = true;
    const dateFilter = {
      StartDate: moment(StartDate).format(DATE_FORMAT),
      EndDate: moment(EndDate).format(DATE_FORMAT),
    };
    // const url = `${URLConstants.ShiftManagement}/List?PageNo=1&PageSize=100&Column="shift_name"&OrderBy="asc"`;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/List/Copy?StoreId=${storeId}&StartDate=${dateFilter.StartDate}&EndDate=${dateFilter.EndDate}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.getShiftScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.shiftScheduleList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getShiftScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.getShiftScheduleListState.inProgress = false;
        })
      );
  };

  GetShiftScheduleListByStoreIdDateUserIdService = (
    StoreId: number,
    StartDate: Date,
    EndDate: Date,
    UserId: number
  ) => {
    this.getShiftScheduleListState.inProgress = true;
    const dateFilter = {
      StartDate: moment(StartDate).format(DATE_FORMAT),
      EndDate: moment(EndDate).format(DATE_FORMAT),
    };
    // const url = `${URLConstants.ShiftManagement}/List?PageNo=1&PageSize=100&Column="shift_name"&OrderBy="asc"`;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/List?StoreId=${StoreId}&StartDate=${dateFilter.StartDate}&EndDate=${dateFilter.EndDate}&userId=${UserId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.getShiftScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.shiftScheduleList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getShiftScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.getShiftScheduleListState.inProgress = false;
        })
      );
  };

  GetShiftScheduleListByStoreIdDateReservationService = (
    StoreId: number,
    StartDate: Date | string,
    EndDate: Date | string
    // UserId: number | null
  ) => {
    this.getShiftScheduleListState.inProgress = true;
    const dateFilter = {
      StartDate: moment(StartDate).format(DATE_FORMAT),
      EndDate: moment(EndDate).format(DATE_FORMAT),
    };
    // const url = `${URLConstants.ShiftManagement}/List?PageNo=1&PageSize=100&Column="shift_name"&OrderBy="asc"`;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/List?StoreId=${StoreId}&StartDate=${dateFilter.StartDate}&EndDate=${dateFilter.EndDate}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.getShiftScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.shiftScheduleList = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.getShiftScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.getShiftScheduleListState.inProgress = false;
        })
      );
  };

  AddShiftScheduleService = (shiftSchedule: any[]) => {
    let data = {
      Schedule: shiftSchedule.map((shift) => {
        return {
          ShiftId: shift.Id,
          UserId: shift.StaffId,
          StoreId: shift.StoreId,
          ScheduleType: 0,
          // ShiftDate: moment(shift.Date).format(DATE_FORMAT),
          ShiftDate: new Date(shift.Date).toISOString(),
          FreeStartTime: shift.FreeStartTime ? shift.FreeStartTime : "00:00:00",
          FreeEndTime: shift.FreeEndTime ? shift.FreeEndTime : "00:00:00",
        };
      }),
    };
    this.addShiftScheduleListState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule`;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.addShiftScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addShiftScheduleListState.success = true;
        }
      })
      .catch((err: string) => {
        this.addShiftScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.addShiftScheduleListState.inProgress = false;
        })
      );
  };

  AddFreeShiftScheduleService = (shiftSchedule: any[]) => {
    let data = {
      Schedule: shiftSchedule.map((shift) => {
        return {
          ShiftId: shift.Id,
          UserId: shift.StaffId,
          StoreId: shift.StoreId,
          ScheduleType: 0,
          // ShiftDate: moment(shift.Date).format(DATE_FORMAT),
          ShiftDate: shift.Date.toISOString(),
          FreeStartTime: shift.FreeStartTime ? shift.FreeStartTime : "00:00:00",
          FreeEndTime: shift.FreeEndTime ? shift.FreeEndTime : "00:00:00",
        };
      }),
    };
    this.addFreeShiftScheduleListState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule`;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.addFreeShiftScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.addFreeShiftScheduleListState.success = true;
        }
      })
      .catch((err: string) => {
        this.addFreeShiftScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.addFreeShiftScheduleListState.inProgress = false;
        })
      );
  };

  OverWriteShiftService = (overWriteShifts: any) => {
    let data = {
      NewSchedule: overWriteShifts.NewShifts.map(
        (shift: {
          ShiftId: any;
          StaffId: any;
          StoreId: any;
          Date: string;
          FreeStartTime: any;
          FreeEndTime: any;
        }) => {
          return {
            ShiftId: shift.ShiftId,
            UserId: shift.StaffId,
            StoreId: shift.StoreId,
            ScheduleType: 0,
            ShiftDate: new Date(shift.Date).toISOString(),
            FreeStartTime:
              shift.ShiftId === -1 ? shift.FreeStartTime : "00:00:00",
            FreeEndTime: shift.ShiftId === -1 ? shift.FreeEndTime : "00:00:00",
          };
        }
      ),
      OldSchedule: overWriteShifts.OldShifts.map((shift: any) => shift.Id),
    };
    this.overWriteShiftScheduleListState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/Overwrite`;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.overWriteShiftScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.overWriteShiftScheduleListState.success = true;
        }
      })
      .catch((err: string) => {
        this.overWriteShiftScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.overWriteShiftScheduleListState.inProgress = false;
        })
      );
  };

  ChangeShiftService = (overWriteShifts: any) => {
    let data = {
      NewSchedule: overWriteShifts.NewShifts.map(
        (shift: {
          ShiftId: any;
          StaffId: any;
          StoreId: any;
          Date: string;
          FreeStartTime: any;
          FreeEndTime: any;
        }) => {
          return {
            ShiftId: shift.ShiftId,
            UserId: shift.StaffId,
            StoreId: shift.StoreId,
            ScheduleType: 0,
            ShiftDate: new Date(shift.Date).toISOString(),
            FreeStartTime:
              shift.ShiftId === -1 ? shift.FreeStartTime : "00:00:00",
            FreeEndTime: shift.ShiftId === -1 ? shift.FreeEndTime : "00:00:00",
          };
        }
      ),
      OldSchedule: overWriteShifts.OldShifts.map((shift: any) => shift.Id),
    };
    this.changeShiftScheduleListState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/Overwrite`;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.changeShiftScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.changeShiftScheduleListState.success = true;
        }
      })
      .catch((err: string) => {
        this.changeShiftScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.changeShiftScheduleListState.inProgress = false;
        })
      );
  };

  PublishShiftScheduleService = (values: any) => {
    let data = {
      storeId: Number(values.StoreId),
      StartDate: moment(values.StartDate).format(DATE_FORMAT),
      EndDate: moment(values.EndDate).format(DATE_FORMAT),
    };
    this.publishScheduleListState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/Publish`;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.publishScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.publishScheduleListState.success = true;
        }
      })
      .catch((err: string) => {
        this.publishScheduleListState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.publishScheduleListState.inProgress = false;
        })
      );
  };

  UnPublishShiftScheduleService = (values: any) => {
    let data = {
      storeId: Number(values.StoreId),
      StartDate: moment(values.StartDate).format(DATE_FORMAT),
      EndDate: moment(values.EndDate).format(DATE_FORMAT),
    };
    this.publishScheduleListState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/UnPublish`;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.publishScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.publishScheduleListState.success = true;
        }
      })
      .catch((err: string) => {
        this.publishScheduleListState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.publishScheduleListState.inProgress = false;
        })
      );
  };

  CopyShiftScheduleService = (values: any) => {
    let data = {
      ...values,
    };
    this.copyScheduleListState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/ShiftSchedule/CopyPreviousMonth`;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<ShiftSchedule[]>>) => {
        if (response.data.Error) {
          this.copyScheduleListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.copyScheduleListState.success = true;
        }
      })
      .catch((err: string) => {
        this.copyScheduleListState.error = err;
      })
      .finally(
        action(() => {
          this.copyScheduleListState.inProgress = false;
        })
      );
  };

  resetShiftBusinessHours = () =>{
    this.shiftBusinessHours = [];
  }

  setShiftBusinessHours = (data:BusinessVM[]) =>{
    this.shiftBusinessHours = data ? [...data] : [];
  }

  get getShiftScheduleList() {
    return this.shiftScheduleList.length > 0
      ? this.shiftScheduleList
          .filter((shiftSchedule) => {
            // Get the day of week (0-6) for this shift
            const shiftDate = new Date(shiftSchedule.ShiftDate);
            const dayOfWeek = shiftDate.getDay();

            const businessDay = this.shiftBusinessHours?.find(
              (business) => business.WeekDays === dayOfWeek
            );

            let isClosed = businessDay && businessDay.IsClosed ? false : true;
            if (
              IsItInHoliday(
                moment(shiftSchedule.ShiftDate).format("YYYY-MM-DD"),
              ) != isClosed
            ) {
              return true;
            } else {
              return businessDay && businessDay.IsClosed ? false : true;
            }
          })
          .map((shiftSchedule) => {
            return {
              Id: shiftSchedule.Id,
              ShiftId: shiftSchedule.ShiftId,
              StaffId: shiftSchedule.UserId,
              Date: moment(shiftSchedule.ShiftDate).format(DateTimeFormatTZ),
              Type: shiftSchedule.ScheduleType,
              Name: shiftSchedule.ShiftRegistration.ShiftName,
              ShiftName: shiftSchedule.ShiftRegistration.ShiftName,
              ShiftShortName: shiftSchedule.ShiftRegistration.ShiftShortName,
              StartTime:
                shiftSchedule.ShiftId > 0
                  ? shiftSchedule.ShiftRegistration.StartTime
                  : shiftSchedule.FreeStartTime,
              EndTime:
                shiftSchedule.ShiftId > 0
                  ? shiftSchedule.ShiftRegistration.EndTime
                  : shiftSchedule.FreeEndTime,
              Colour: shiftSchedule.ShiftRegistration.Colour,
              BusinessTiming: shiftSchedule.BusinessVM,
              StoreId: shiftSchedule.StoreId,
              ScheduleType: shiftSchedule.ScheduleType,
              ShiftStatus: shiftSchedule.ShiftRegistration?.ShiftStatus,
            };
          })
      : [];
  }
 get getShiftScheduleDateRange() {
  if (!this.shiftScheduleList || this.shiftScheduleList.length === 0) {
    return { firstDate: null, lastDate: null };
  }

  // Extract all dates
  const allDates = this.shiftScheduleList.map(
    (s) => moment(s.ShiftDate).startOf("day") // normalize to day
  );

  // Find min & max
  const firstDate = moment.min(allDates).format(DATE_FORMAT);
  const lastDate = moment.max(allDates).format(DATE_FORMAT);

  return { firstDate, lastDate };
}


  get getClosedDays() {
    if (this.shiftBusinessHours.length > 0) {
      return this.shiftBusinessHours?.filter(
        (businessDay) => businessDay.IsClosed
      ).map((businessDay) => {
        return {
          WeekDays: businessDay.WeekDays,
          IsClosed: businessDay.IsClosed,
        };
      });
    } else {
      return [];
    }
  }

  /**
   * This function is used to map staffList to allStaffManagement List suitable for Grid Component.
   * @returns allStaffManagementsList
   */
  get allShiftManagement(): IShiftManagementList[] {
    if (this.shiftList && this.shiftList?.length > 0)
      return this.shiftList.map((shift) => {
        let language = getLanguageFromLocalStorage();
        let isDelete = true;
        let isEdit = true;
        return {
          Id: shift.Id,
          ShiftId: shift.Id,
          ShiftName: shift.ShiftName || "",
          ShiftShortName: shift.ShiftShortName || "",
          StartTime: shift.StartTime || "",
          EndTime: shift.EndTime || "",
          Colour: shift.Colour || "#FFFFFF",
          Memo: shift.Memo || "",
          ShiftStatus: shift.ShiftStatus ?? 1,
          Language: language,
          isDelete: isDelete,
          isEdit: isEdit,
          StoreId: shift.StoreId || -1,
        };
      });
    return [];
  }
  get allShiftOption(): IOptionShiftSchedule[] {
    let options: IOptionShiftSchedule[] = [
      // {
      //   id: -1,
      //   value: formatMessage("please_select_shift"),
      //   disabled: false,
      // },
    ];
    let data =
      this.shiftList && this.shiftList?.length > 0
        ? this.shiftList.map((shift) => {
            let language = getLanguageFromLocalStorage();
            let isDelete = true;
            let isEdit = true;
            let data = {
              Id: shift.Id,
              ShiftId: shift.Id,
              ShiftName: shift.ShiftName || "",
              ShiftShortName: shift.ShiftShortName || "",
              StartTime: shift.StartTime || "",
              EndTime: shift.EndTime || "",
              Colour: shift.Colour || "#FFFFFF",
              Memo: shift.Memo || "",
              Language: language,
              isDelete: isDelete,
              isEdit: isEdit,
              StoreId: shift.StoreId || -1,
            };
            return {
              id: shift.Id,
              value: `${shift.ShiftName}(${shift.ShiftShortName})`,
              label: `${shift.ShiftName}(${shift.ShiftShortName})`,
              data: data,
            };
          })
        : [];

    options = options.concat(data);

    return options;
  }

  get allShiftListAssignOption(): IOptionShiftSchedule[] {
    let options: IOptionShiftSchedule[] = [
      {
        id: -1,
        value: formatMessage("please_select_shift"),
        disabled: false,
      },
    ];
    let data =
      this.shiftList && this.shiftList?.length > 0
        ? this.shiftList.map((shift) => {
            let language = getLanguageFromLocalStorage();
            let isDelete = true;
            let isEdit = true;
            let data = {
              Id: shift.Id,
              ShiftId: shift.Id,
              ShiftName: shift.ShiftName || "",
              ShiftShortName: shift.ShiftShortName || "",
              StartTime: shift.StartTime || "",
              EndTime: shift.EndTime || "",
              Colour: shift.Colour || "#FFFFFF",
              Memo: shift.Memo || "",
              Language: language,
              isDelete: isDelete,
              isEdit: isEdit,
              StoreId: shift.StoreId || -1,
            };
            return {
              id: shift.Id,
              value: `${shift.ShiftName}(${shift.ShiftShortName})`,
              label: `${shift.ShiftName}(${shift.ShiftShortName})`,
              data: data,
            };
          })
        : [];

    options = options.concat(data);

    return options;
  }

  /**
   * This function is used to Get StaffManagement Details by Id by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  GetShiftManagementByIdService = (id: number) => {
    this.shiftState.inProgress = true;
    const url = `${URLConstants.ShiftManagement}/${id}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IShiftManagementVM>>) => {
          if (response.data.Error) {
            this.shiftState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            let data = response.data.Data;
            this.shift = data;
          }
        }
      )
      .catch((err: string) => {
        this.shiftState.error = err;
      })
      .finally(
        action(() => {
          this.shiftState.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add StaffManagement Form.
   * @returns StaffManagement Details
   *
   */
  get shiftDetail(): IAddShiftManagement {
    if (this.shift) {
      return {
        ShiftName: this.shift.ShiftName || "",
        ShiftShortName: this.shift.ShiftShortName || "",
        StartTime:
          moment(this.shift.StartTime, "HH:mm:ss").format("HH:mm") || "00:00",
        EndTime:
          moment(this.shift.EndTime, "HH:mm:ss").format("HH:mm") || "00:00",
        Colour: this.shift.Colour || "#FFFFFF",
        StoreId: this.shift.StoreId || -1,
        Memo: this.shift.Memo || "",
        ShiftStatus: this.shift.ShiftStatus?.toString()
      };
    }
    return {
      ShiftName: "",
      ShiftShortName: "",
      StartTime: "00:00",
      EndTime: "00:00",
      Colour: "#FFFFFF",
      StoreId: -1,
      Memo: "",
      ShiftStatus: "0"
    };
  }

  /*
    This function is used to reset all staff observables to their initial values.  
    */
  resetShiftManagementDetail = () => {
    this.shift = undefined;
    this.shiftState = { ...this.initialStateValue };
  };

  /**
   * This function is used to Add New StaffManagement by calling an API.
   * @param data : StaffManagement Data
   * @returns
   */
  AddShiftManagementService = (data: IAddShiftManagement) => {
    this.addUpdateShiftManagementState.inProgress = true;
    let url = URLConstants.ShiftManagement;
    let shiftData: IAddShiftManagement = {
      ...data,
    };
    return baseService
      .postRequest(url, shiftData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUpdateShiftManagementState.error = response.data.Message;
        } else this.addUpdateShiftManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateShiftManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateShiftManagementState.inProgress = false;
        })
      );
  };

  /**
   * This function is used to Updating Existing StaffManagement Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param data
   * @returns
   */
  UpdateShiftManagementService = (id: number, data: IAddShiftManagement) => {
    this.addUpdateShiftManagementState.inProgress = true;
    let url = URLConstants.ShiftManagement;
    let staffData: any = {
      ...data,
      Id: id,
      StartTime: moment(data.StartTime, "HH:mm").format("HH:mm:ss"),
      EndTime: moment(data.EndTime, "HH:mm").format("HH:mm:ss"),
    };
    return baseService
      .putRequest(url, staffData)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUpdateShiftManagementState.error = response.data.Message;
        } else this.addUpdateShiftManagementState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateShiftManagementState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateShiftManagementState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetAddUpdateShiftManagementState = () => {
    this.addUpdateShiftManagementState.inProgress = false;
    this.addUpdateShiftManagementState.success = false;
    this.addUpdateShiftManagementState.error = "";
  };

  /**
   * This function is used to Delete StaffManagement by calling an API.
   * @param id : The StaffManagement Identifier
   * @returns
   */
  DeleteShiftManagementService = (id: number) => {
    this.deleteShiftManagementState.inProgress = true;
    let url = `${URLConstants.ShiftManagement}/${id}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteShiftManagementState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteShiftManagementState.success = true;
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
        this.deleteShiftManagementState.error = err;
      })
      .finally(
        action(() => {
          this.deleteShiftManagementState.inProgress = false;
        })
      );
  };

  DeleteScheduleService = (scheduleId: number) => {
    this.deleteShiftScheduleState.inProgress = true;
    let url = `${URLConstants.ShiftManagement}/ShiftSchedule/${scheduleId}`;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteShiftScheduleState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteShiftScheduleState.success = true;
      })
      .catch((err: string) => {
        this.deleteShiftScheduleState.error = err;
      })
      .finally(
        action(() => {
          this.deleteShiftScheduleState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all AddStaffManagement observables to their initial values.  
    */
  resetDeleteShiftManagement = () => {
    this.deleteShiftManagementState.inProgress = false;
    this.deleteShiftManagementState.success = false;
    this.deleteShiftManagementState.error = "";
  };

  /**
   * This function is used to Updating Existing StaffManagement state (i.e. active/inactive) Details by calling an API.
   * @param id : The StaffManagement Identifier
   * @param state: StaffManagement state
   * @returns
   */
  UpdateShiftManagementStatusService = (id: number, state: boolean) => {
    this.activeInactiveState.inProgress = true;
    let url = URLConstants.ShiftManagementStatus;
    let shiftData: any = {
      Id: id,
      IsActive: state,
    };
    return baseService
      .putRequest(url, shiftData)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.activeInactiveState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.activeInactiveState.success = true;
      })
      .catch((err: string) => {
        this.activeInactiveState.error = err;
      })
      .finally(
        action(() => {
          this.activeInactiveState.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all observables to their initial values.  
    */
  reset = () => {
    this.error = "";
    this.inProgress = false;
    this.addUpdateShiftManagementState.success = false;
    this.addUpdateShiftManagementState.error = "";
    this.deleteShiftManagementState = { ...this.initialStateValue };
    this.addUpdateShiftManagementState.inProgress = false;
    this.shiftState = { ...this.initialStateValue };
    this.addUpdateShiftManagementState = { ...this.initialStateValue };
    this.deleteShiftManagementState = { ...this.initialStateValue };
    this.deleteShiftScheduleState = { ...this.initialStateValue };
    this.getShiftScheduleListState = { ...this.initialStateValue };
    this.addShiftScheduleListState = { ...this.initialStateValue };
    this.publishScheduleListState = { ...this.initialStateValue };
    this.copyScheduleListState = { ...this.initialStateValue };
  };

  resetCopyScheduleListState = () => {
    this.copyScheduleListState.inProgress = false;
    this.copyScheduleListState.success = false;
    this.copyScheduleListState.error = "";
  };
  resetPublishScheduleListState = () => {
    this.publishScheduleListState.inProgress = false;
    this.publishScheduleListState.success = false;
    this.publishScheduleListState.error = "";
  };

  resetAddShiftScheduleListState = () => {
    this.addShiftScheduleListState.inProgress = false;
    this.addShiftScheduleListState.success = false;
    this.addShiftScheduleListState.error = "";
  };

  resetFreeAddShiftScheduleListState = () => {
    this.addFreeShiftScheduleListState.inProgress = false;
    this.addFreeShiftScheduleListState.success = false;
    this.addFreeShiftScheduleListState.error = "";
  };

  resetGetShiftScheduleListState = () => {
    this.getShiftScheduleListState.inProgress = false;
    this.getShiftScheduleListState.success = false;
    this.getShiftScheduleListState.error = "";
  };

  resetDeleteShiftScheduleState = () => {
    this.deleteShiftScheduleState.inProgress = false;
    this.deleteShiftScheduleState.success = false;
    this.deleteShiftScheduleState.error = "";
  };

  resetDeleteShiftManagementState = () => {
    this.deleteShiftManagementState.inProgress = false;
    this.deleteShiftManagementState.success = false;
    this.deleteShiftManagementState.error = "";
  };
  resetOverWriteShiftScheduleListState = () => {
    this.overWriteShiftScheduleListState.inProgress = false;
    this.overWriteShiftScheduleListState.success = false;
    this.overWriteShiftScheduleListState.error = "";
  };
  resetChangeShiftScheduleListState = () => {
    this.changeShiftScheduleListState.inProgress = false;
    this.changeShiftScheduleListState.success = false;
    this.changeShiftScheduleListState.error = "";
  };

  resetGetShiftScheduleList = () => {
    this.getShiftScheduleListState = { ...this.initialStateValue };
    this.shiftScheduleList = [];
    this.shiftList = [];
    this.addShiftScheduleListState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all staff observables to their initial values.
   * @returns
   */
  resetShift = () => {
    this.error = "";
    this.inProgress = false;
    this.shiftList = GetAllShiftManagementState.Shifts;

    this.addUpdateShiftManagementState = { ...this.initialStateValue };
    this.deleteShiftManagementState = { ...this.initialStateValue };
    this.shift = undefined;
    this.shiftState = { ...this.initialStateValue };
  };
}

export default new ShiftManagementStore();
